import { observer } from 'mobx-react-lite';
import {
    useEffect,
    useState
} from 'react';
import { bytesToMB, generateGUID } from '@/utils/utils';
import { File, CheckFolerMonth } from '@/types/files';
import { useStore } from '@/hooks/useStore';
import { fileTypeFormat, uploadStatus } from '@/types/enums';
import Dropzone from 'react-dropzone';
import { Button } from '@/components/common/Button';
import { ColumnDef } from '@tanstack/react-table';
import Table from '@/components/table/Table';
import { Trash2, RefreshCcw, FileX } from 'lucide-react';
import { ACTIVE, NOT_DELETE } from '@/configs/constants';
import { getIdFolderDriveFromURL } from '@/utils/file';
import { flowResult } from 'mobx';
import doneIcon from '../../assets/images/done.png';
import failIcon from '../../assets/images/alert.png';
import loadingIcon from '../../assets/images/loading.png';
import folderUp from '../../assets/images/folder-up.png';
import GoogleAPI from '@/services/drive';
import { t } from 'i18next';
import { Tooltip as ReactTooltip } from 'react-tooltip';

export default observer(function FilesManagement() {
    const {
        authStore: { profile, setLinkRootFolder, logout },
        fileStore: { getCustomerFolderId, postNewFile, checkFileExist },
        modalStore: { showErrorModal, hideModal },
        userStore: { updateFolderIdCustomer },
        companyStore: { updateRootFolder, getRootFolderId },
        apiStore: { setLoading }
    } = useStore();
    const [listUploadTemp, setListUploadTemp] = useState<File[]>([]);
    const [listFolderMonth, setListFolderMonth] = useState<CheckFolerMonth[]>([]);
    const [isUpoading, setIsUploading] = useState<boolean>(false);

    const [ROOT_FOLDER_ID, setRootFolderId] = useState('');
    const googleAPIService = GoogleAPI.getInstance();
    useEffect(() => {
        checkAndCreateRootFolder();
    }, []);

    const checkAndCreateRootFolder = async () => {
        const ggToken = await googleAPIService.getToken();
        if (!ggToken) {
            flowResult(logout());
            showErrorModal({
                content: t('messages.cannot_access_google_drive')
            });
            return;
        }

        const resFolderId = await flowResult(getRootFolderId());
        if (!resFolderId) {
            setLoading(true);
            const rootFolderId = await googleAPIService.createFolderRoot();
            if (rootFolderId) {
                await flowResult(updateRootFolder(rootFolderId));
                setLinkRootFolder(`https://drive.google.com/drive/folders/${rootFolderId}`);
                setRootFolderId(rootFolderId);
                setLoading(false);
                return rootFolderId;
            }
        } else {
            setLoading(true);
            const permission = await googleAPIService.checkPermissionUploadById(getIdFolderDriveFromURL(resFolderId), profile?.email);
            if (!permission) {
                const rootFolderId = await googleAPIService.createFolderRoot();
                if (rootFolderId) {
                    await flowResult(updateRootFolder(rootFolderId));
                    setLinkRootFolder(`https://drive.google.com/drive/folders/${rootFolderId}`);
                    setRootFolderId(rootFolderId);
                    setLoading(false);
                    return getIdFolderDriveFromURL(rootFolderId);
                }
                setLoading(false);
                return;
            } else {
                setRootFolderId(getIdFolderDriveFromURL(resFolderId) ?? '');
                setLinkRootFolder(resFolderId);
                setLoading(false);
                return getIdFolderDriveFromURL(resFolderId);
            }
        }
    };

    useEffect(() => {
        if (listUploadTemp.filter(e => e.uploaded === uploadStatus.WAITING_REUP).length > 0 &&
            !listUploadTemp.find(e => e.uploaded === uploadStatus.UPLOADING || e.uploaded === uploadStatus.REPLACING)) {
            handleClickUpload(true);
        }
        if (listUploadTemp.some(e => (e.uploaded === uploadStatus.UPLOADING || e.uploaded === uploadStatus.REPLACING))) {
            setIsUploading(false);
        }
    }, [listUploadTemp]);

    const handleUploadFile = async (file, delay = false, idFolderRoot = ROOT_FOLDER_ID) => {
        const name = file.path; // Get file name
        const dataSplit = await googleAPIService.splitNameFile(name); // Split file name to user and month
        if (!dataSplit) return false;

        const current = { user: dataSplit.userFolder, month: dataSplit.monthFolder };
        if (listFolderMonth.find(e => e.user === dataSplit.userFolder)) {
            // Have a file is uploading whit same customer
            return undefined;
        } else {
            listFolderMonth.push(current);
        }

        // Get folder id of customer
        let customerDetail = await flowResult(getCustomerFolderId(dataSplit.userFolder));
        if (!customerDetail || customerDetail.company_id !== profile?.company_id ||
            customerDetail.status !== ACTIVE || customerDetail.delete_flag !== NOT_DELETE) {
            return false;
        }
        let folderIdCus = customerDetail?.folder_url_id!;
        const checkCusFolder = await googleAPIService.checkFolderExist(folderIdCus, idFolderRoot);
        if (!checkCusFolder) {
            const res = await googleAPIService.createFolderInParent(dataSplit.userFolder, idFolderRoot);
            await flowResult(updateFolderIdCustomer({ folder_id: res, customerId: customerDetail.id }));
            if (res) folderIdCus = res;
        }

        // Get folder_month's id of customer
        const monthFolderID = await googleAPIService.findFolderByNameAndParentId(folderIdCus, dataSplit.monthFolder);
        if (!monthFolderID) {
            setTimeout(() => {
                setListUploadTemp(prevItems =>
                    prevItems.map(item =>
                        item.tempId === file.tempId ? { ...item, uploaded: uploadStatus.FAIL } : item));
            }, delay ? 1000 : 0);
            return;
        }

        // ✅ Check if file name exists
        // If it exists, don't set parents to avoid moving the file
        // If it doesn't exist, set parents to upload to the month folder
        // If the file name is the same, it will be replaced
        // If the file name is different, it will be uploaded to the month folder
        const existingFileId = await googleAPIService.findExistingFile(file.path, monthFolderID);
        if (existingFileId) {
            setListUploadTemp(prevItems =>
                prevItems.map(item =>
                    item.tempId === file.tempId ? { ...item, uploaded: uploadStatus.REPLACING } : item));
        }

        const metadata = {
            name: file.path,
            mimeType: file.mimeType,
            parents: existingFileId ? undefined : [monthFolderID] // If file name exists, don't set parents
        };

        const form = new FormData();
        form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
        form.append('file', file.originFile);

        return new Promise(async (resolve, reject) => {
            const ggToken = await googleAPIService.getToken();
            if (!ggToken) {
                flowResult(logout());
                return;
            }
            const xhr = new XMLHttpRequest();
            xhr.open(
                existingFileId ?
                    'PATCH' : // 🔄 Repalce file if exist
                    'POST', // ➕ Upload new file
                existingFileId ?
                    `https://www.googleapis.com/upload/drive/v3/files/${existingFileId}?uploadType=multipart` :
                    'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart'
            );

            xhr.setRequestHeader('Authorization', `Bearer ${ggToken}`);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    const percentComplete = (event.loaded / event.total) * 100;
                    setListUploadTemp(prevItems =>
                        prevItems.map(item =>
                            item.tempId === file.tempId ? { ...item, progress: percentComplete } : item));
                }
            };

            xhr.onload = async () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    const response = JSON.parse(xhr.responseText);
                    const body = {
                        name: file.path,
                        customer_id: parseInt(`${customerDetail?.id}`),
                        company_id: parseInt(`${customerDetail?.company_id}`),
                        path: response.id,
                        upload_month: dataSplit.monthFolder.slice(4),
                        upload_year: dataSplit.monthFolder.slice(0, 4)
                    };
                    await flowResult(postNewFile(body));
                    resolve(response.id);
                } else {
                    reject(false);
                }
            };

            xhr.onerror = () => reject(false);
            xhr.send(form);
        });
    };

    const onPreUpload = (files) => {
        if (!ROOT_FOLDER_ID) {
            showErrorModal({
                content: t('messages.share_folder_invalid')
            });
            return;
        }
        const filesToUpload2 = Array.from(files);
        filesToUpload2.forEach(async (f: any) => {
            const typeFile = f.type;
            const filename = f.name;
            const nameValid = await googleAPIService.checkFileNameInput(filename);
            if (typeFile !== '') {
                const tempId = generateGUID();
                const sizeInMB: number = bytesToMB(f.size);
                let temp = {
                    ...f,
                    mimeType: f.type,
                    path: f.name,
                    name: f.name,
                    id: '',
                    tempId: tempId,
                    originFile: f,
                    uploaded:
                        sizeInMB > Number.parseFloat(
                            process.env.REACT_APP_MAX_SIZE_FILE_IN_MB || '50'
                        ) || typeFile !== fileTypeFormat.PDF || !nameValid ?
                            uploadStatus.ERROR_FILE :
                            uploadStatus.WAITING
                };
                setListUploadTemp(prev => [...prev, temp]);
                if (sizeInMB >
                    Number.parseFloat(
                        process.env.REACT_APP_MAX_SIZE_FILE_IN_MB || '50'
                    ) || typeFile !== fileTypeFormat.PDF || !nameValid) {
                    showErrorModal({
                        id: 'file_error',
                        content: t('messages.file_error')
                    });
                }
            }
        });
    };

    const getColorTextTableFiles = (statusFile: string | undefined) => {
        switch (statusFile) {
            case uploadStatus.WAITING:
                return 'text-black';
            case uploadStatus.DONE:
                return 'text-green-500';
            case uploadStatus.REPLACED:
                return 'text-green-500';
            case uploadStatus.ERROR_FILE:
                return 'text-yellow-600';
            case uploadStatus.FAIL:
                return 'text-red400';
            case uploadStatus.UPLOADING:
                return 'text-blue-500';
            case uploadStatus.WAITING_REUP:
                return 'text-blue-500';
            case uploadStatus.REPLACING:
                return 'text-blue-500';
            default:
                return 'text-black';
        }
    };

    const onDeleteFiles = (original: File) => {
        setListUploadTemp(prevItems =>
            prevItems.filter(item => item.tempId !== original.tempId));
    };

    const onRetry = async (e: File) => {
        const id = await checkAndCreateRootFolder();
        setListFolderMonth([]);
        setListUploadTemp(prevItems =>
            prevItems.map(item =>
                item.tempId === e.tempId ?
                    { ...item, uploaded: uploadStatus.UPLOADING, progress: 0 } :
                    item));
        const res = await handleUploadFile(e, true, id);
        if (res) {
            setListUploadTemp(prevItems =>
                prevItems.map(item =>
                    item.tempId === e.tempId ?
                        { ...item, uploaded: e.uploaded === uploadStatus.REPLACING ? uploadStatus.REPLACED : uploadStatus.DONE, id: `${res}` } :
                        item));
        } else {
            setTimeout(() => {
                setListUploadTemp(prevItems =>
                    prevItems.map(item =>
                        item.tempId === e.tempId ?
                            { ...item, uploaded: uploadStatus.FAIL } :
                            item));
            }, 1000);
        }
    };

    const columns: ColumnDef<File>[] = [
        {
            accessorKey: 'name',
            header: 'File name',
            size: 200,
            enableSorting: false,
            cell: ({ row: { original, index } }) => (
                <div className='max-w-[800px] block'>
                    <div className='w-full flex'>
                        <p className='mx-[8px]'>
                            {index + 1}
                            .
                        </p>
                        <a
                            href={original.id ? `https://drive.google.com/open?id=${original.id}` : undefined}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={`${getColorTextTableFiles(original.uploaded)} text-[15px] ${original.uploaded === uploadStatus.DONE && 'underline cursor-pointer'}`}
                        >
                            {original.name}
                        </a>
                    </div>
                    {((original.progress > 0 && original.progress < 100) ||
                        (original.progress === 100 && original.uploaded === uploadStatus.UPLOADING)) &&
                        (
                            <div className='w-full mt-[2px]'>
                                <progress className='w-full' max={100} value={original.progress} />
                            </div>
                        )}
                </div>

            )
        },
        {
            header: 'Status',
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='flex justify-start max-w-[160px] w-[160px]'>
                    {(original.uploaded === uploadStatus.UPLOADING ||
                        original.uploaded === uploadStatus.WAITING_REUP ||
                        original.uploaded === uploadStatus.REPLACING) ?
                        (
                            <div className='flex justify-end items-center'>
                                <img src={loadingIcon} alt={loadingIcon} className='animate-[spin_1s_linear_infinite] w-[16px] h-[16px]' />
                                <p className={`text-[15px] pl-[8px] ${getColorTextTableFiles(original.uploaded)}`}>{t('status_upload.waiting_reup_or_uploading')}</p>
                            </div>
                        ) :
                        original.uploaded === uploadStatus.DONE ?
                            (
                                <div className='flex justify-end items-center'>
                                    <img src={doneIcon} alt={doneIcon} className='w-[16px] h-[16px]' />
                                    <p className={`text-[15px] pl-[8px] ${getColorTextTableFiles(original.uploaded)}`}>{t('status_upload.done')}</p>
                                </div>
                            ) :
                            original.uploaded === uploadStatus.REPLACED ?
                                (
                                    <div className='flex justify-end items-center'>
                                        <img src={doneIcon} alt={doneIcon} className='w-[16px] h-[16px]' />
                                        <p className={`text-[15px] pl-[8px] ${getColorTextTableFiles(original.uploaded)}`}>{t('status_upload.replaced')}</p>
                                    </div>
                                ) :
                                original.uploaded === uploadStatus.ERROR_FILE ?
                                    (
                                        <div className='flex justify-end items-center text-yellow-500'>
                                            <FileX className='text-yellow-600 w-[20px] h-[20px]' />
                                            <p className={`text-[15px] pl-[8px] ${getColorTextTableFiles(original.uploaded)}`}>{t('status_upload.error_file')}</p>
                                        </div>
                                    ) :
                                    original.uploaded === uploadStatus.FAIL ?
                                        (
                                            <div className='flex justify-end items-center text-yellow-500'>
                                                <img src={failIcon} alt={failIcon} className='w-[16px] h-[16px]' />
                                                <p className={`text-[15px] pl-[8px] ${getColorTextTableFiles(original.uploaded)}`}>{t('status_upload.fail')}</p>
                                            </div>
                                        ) :
                                        null}
                </div>
            )
        },
        {
            header: 'Actions',
            size: 10,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div
                    className='flex justify-end w-full'
                >
                    <button
                        disabled={original.uploaded !== (uploadStatus.FAIL) ||
                            (listUploadTemp.some(e => e.uploaded === (uploadStatus.UPLOADING || uploadStatus.WAITING_REUP || uploadStatus.REPLACING)))}
                        onClick={() => onRetry(original)}
                        className='text-blackForce cursor-pointer disabled:text-transparent disabled:cursor-default'
                        data-tooltip-id='tootip-re-upload'
                        data-tooltip-hidden={listUploadTemp.some(e => e.uploaded === (uploadStatus.UPLOADING || uploadStatus.WAITING_REUP || uploadStatus.REPLACING))}
                        data-tooltip-delay-show={240}
                    >
                        <RefreshCcw size={20} />
                    </button>
                    <button
                        disabled={original.uploaded !== uploadStatus.FAIL && original.uploaded !== uploadStatus.WAITING && original.uploaded !== uploadStatus.ERROR_FILE}
                        onClick={() => onDeleteFiles(original)}
                        className='text-blackForce mx-[8px] cursor-pointer disabled:text-transparent disabled:cursor-default'
                        data-tooltip-id='tootip-delete'
                        data-tooltip-hidden={listUploadTemp.some(e => e.uploaded === (uploadStatus.UPLOADING || uploadStatus.WAITING_REUP || uploadStatus.REPLACING))}
                        data-tooltip-delay-show={240}
                    >
                        <Trash2 size={20} />
                    </button>
                </div>
            )
        }
    ];

    const uploading = async (listForUpload, ignoreFail, idFolderRoot = ROOT_FOLDER_ID) => {
        setListUploadTemp(prevItems => // Set status uploading for all item valid to upload
            prevItems.map(item =>
                (ignoreFail ?
                    (item.uploaded === uploadStatus.WAITING_REUP) :
                    (item.uploaded === uploadStatus.WAITING || item.uploaded === uploadStatus.FAIL)) ?
                    { ...item, uploaded: uploadStatus.UPLOADING } :
                    item));
        await Promise.all(
            listForUpload.map(async (e: File) => {
                const res = await handleUploadFile(e, false, idFolderRoot);
                if (res && typeof res === 'string') { // Upload to Drive success
                    setListUploadTemp(prevItems =>
                        prevItems.map(item =>
                            item.tempId === e.tempId ?
                                { ...item, uploaded: item.uploaded === uploadStatus.REPLACING ? uploadStatus.REPLACED : uploadStatus.DONE, id: `${res}` } :
                                item));
                } else if (res === undefined) { // Change status to waiting_reup when same folder
                    setListUploadTemp(prevItems =>
                        prevItems.map(item =>
                            item.tempId === e.tempId ?
                                { ...item, uploaded: uploadStatus.WAITING_REUP } :
                                item));
                } else { // Upload fail
                    setListUploadTemp(prevItems =>
                        prevItems.map(item =>
                            item.tempId === e.tempId ?
                                { ...item, uploaded: uploadStatus.FAIL } :
                                item));
                }
            })
        );
    };

    const handleClickUpload = async (ignoreFail = false) => {
        setIsUploading(true);

        const id = await checkAndCreateRootFolder();
        setListFolderMonth([]);
        const listForUpload = listUploadTemp.filter(e => ignoreFail ? // Check is auto re-up
            (e.uploaded === uploadStatus.WAITING_REUP) : // Select waiting re-up status only
            (e.uploaded === uploadStatus.WAITING || e.uploaded === uploadStatus.FAIL)); // Select waiting and fail status when not auto re-up
        if (listForUpload && listForUpload.length > 0) { // Check length of list upload
            const listNames = listForUpload.map(e => e.name) || [];
            const body = {
                names: listNames
            };
            const checkExist = await flowResult(checkFileExist(body));
            if (checkExist && !ignoreFail) {
                showErrorModal({
                    id: 'confirm_replace',
                    title: t('words_title.confirm'),
                    showCloseIcon: false,
                    closeWhenOutside: false,
                    saveButton: false,
                    cancelButton: false,
                    content: (
                        <div className='h-30 w-full'>
                            <p className='mt-6 text-[14px] text-black'>{t('messages.duplicate_file')}</p>
                            <div className='flex flex-row text-center mt-8 w-full justify-end'>
                                <Button
                                    onClick={() => {
                                        setIsUploading(false);
                                        hideModal('confirm_replace');
                                    }}
                                    className='min-w-32 bg-white text-main'
                                    variant='outline'
                                >
                                    いいえ
                                </Button>
                                <Button
                                    onClick={async () => {
                                        hideModal('confirm_replace');
                                        await uploading(listForUpload, ignoreFail, id);
                                    }}
                                    className='min-w-32  ml-[20px] bg-red400 text-white'
                                    variant='outline'
                                >
                                    はい
                                </Button>
                            </div>
                        </div>
                    )
                });
            } else {
                await uploading(listForUpload, ignoreFail, id);
            }
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;
        onPreUpload(files);
    };

    const handleFileInputReset = () => {
        const fileInput = document.getElementById('upload') as HTMLInputElement;
        fileInput.value = '';
        const fileInputFolder = document.getElementById('uploadFolder') as HTMLInputElement;
        fileInputFolder.value = '';
    };

    return (
        <div className=''>
            <Dropzone onDrop={acceptedFiles => onPreUpload(acceptedFiles)}>
                {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps()} className='text-center text-gray-500'>
                        <div className='w-full bg-white border-dashed border-[1px] border-black rounded-xl'>
                            <div className=' flex justify-center text-center text-[15px] font-semibold py-[10px] text-black'>
                                {t('messages.des_upload')}
                            </div>
                            <div className='flex justify-center pb-[10px]'>
                                <div className=''>
                                    <input
                                        disabled={listUploadTemp.filter(e => e.uploaded === uploadStatus.UPLOADING).length > 0}
                                        type='file'
                                        multiple
                                        style={{ display: 'none' }}
                                        id='upload'
                                        accept='.pdf'
                                        onClick={handleFileInputReset}
                                        onChange={handleFileChange}
                                    />
                                    <Button
                                        className='  text-white rounded-sm text-[16px] justify-center content-center cursor-pointer'
                                        variant='second'
                                    >
                                        <label
                                            htmlFor='upload'
                                            className=' w-full h-full text-white rounded-sm text-[13px] justify-center content-center cursor-pointer'
                                        >
                                            {t('messages.select_file')}
                                        </label>
                                    </Button>

                                </div>
                                <div className=' ml-[16px]'>
                                    <input
                                        disabled={listUploadTemp.filter(e => e.uploaded === uploadStatus.UPLOADING).length > 0}
                                        type='file'
                                        webkitdirectory='true'
                                        style={{ display: 'none' }}
                                        id='uploadFolder'
                                        onClick={handleFileInputReset}
                                        onChange={handleFileChange}
                                    />
                                    <Button variant='second' className=''>
                                        <label
                                            htmlFor='uploadFolder'
                                            className=' w-full h-full text-white rounded-sm text-[13px] justify-center content-center cursor-pointer'
                                        >
                                            {t('messages.select_folder')}
                                        </label>
                                    </Button>

                                </div>
                            </div>
                            <div className='text-[13px] mb-[12px]'>{t('messages.file_limit')}</div>
                        </div>
                    </div>
                )}
            </Dropzone>
            <div className='w-full flex justify-between pt-4 '>
                <div className='w-[70%] max-h-[calc(100vh-68px-145px-16px)] overflow-y-scroll'>
                    {listUploadTemp && (
                        <Table
                            columns={columns}
                            data={listUploadTemp}
                            showSelectPageSize={false}
                            getRowId={row => row.tempId?.toString()}
                            headerClassName='hidden'
                            showPagination={false}
                            showPagingInfo={false}
                            noData=''
                            cellClassName='truncate'
                            fixOverText
                        />
                    )}
                </div>
                <Button
                    type='submit'
                    variant='primary'
                    onClick={() => handleClickUpload()}
                    disabled={!listUploadTemp ||
                        listUploadTemp.length <= 0 ||
                        !listUploadTemp.some(e => (e.uploaded === uploadStatus.WAITING)) ||
                        listUploadTemp.some(e => (e.uploaded === uploadStatus.ERROR_FILE)) ||
                        listUploadTemp.some(e => (e.uploaded === uploadStatus.WAITING_REUP)) ||
                        listUploadTemp.some(e => (e.uploaded === uploadStatus.UPLOADING)) ||
                        listUploadTemp.some(e => (e.uploaded === uploadStatus.REPLACING)) || isUpoading}
                    className='text-[13px]'
                >
                    <img src={folderUp} alt={folderUp} className='filter invert contrast-125 hue-rotate-180 mr-[12px]' />
                    {t('messages.upload')}
                </Button>
            </div>

            <ReactTooltip
                id='tootip-delete'
                place='top'
                content='削除'
            />
            <ReactTooltip
                id='tootip-re-upload'
                place='top'
                content='再アップロード'
            />
        </div>
    );
});
